import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import moment from 'moment';
import {
  getPricingRules,
  createPricingRule,
  updatePricingRule,
  deletePricingRule,
  getAdminCourts,
} from '../services/API';

const DateRangePricing = () => {
  const [pricingRules, setPricingRules] = useState([]);
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({
    court_id: '',
    sport: '',
    start_date: null,
    end_date: null,
    weekday_price_until_17: '',
    weekday_price_after_17: '',
    weekend_price_until_17: '',
    weekend_price_after_17: '',
  });

  const token = window.localStorage.getItem('token');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rulesData, courtsData] = await Promise.all([
        getPricingRules(token),
        getAdminCourts(token),
      ]);
      setPricingRules(rulesData || []);
      setCourts(courtsData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (rule = null) => {
    if (rule) {
      setEditingRule(rule);
      setFormData({
        court_id: rule.court_id,
        sport: rule.sport,
        start_date: moment(rule.start_date),
        end_date: moment(rule.end_date),
        weekday_price_until_17: rule.weekday_price_until_17,
        weekday_price_after_17: rule.weekday_price_after_17,
        weekend_price_until_17: rule.weekend_price_until_17,
        weekend_price_after_17: rule.weekend_price_after_17,
      });
    } else {
      setEditingRule(null);
      setFormData({
        court_id: '',
        sport: '',
        start_date: null,
        end_date: null,
        weekday_price_until_17: '',
        weekday_price_after_17: '',
        weekend_price_until_17: '',
        weekend_price_after_17: '',
      });
    }
    setOpenDialog(true);
  };

  const handleSportChange = (sport) => {
    setFormData((prev) => ({
      ...prev,
      sport: sport,
      court_id: '', // Reset court selection when sport changes
    }));
  };

  const getFilteredCourts = () => {
    if (!formData.sport) return [];
    return courts.filter(court => court.sport === formData.sport);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRule(null);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    const submitData = {
      ...formData,
      start_date: formData.start_date.format('YYYY-MM-DD'),
      end_date: formData.end_date.format('YYYY-MM-DD'),
    };

    try {
      if (editingRule) {
        const result = await updatePricingRule(token, editingRule.id, submitData);
        if (!result?.error) {
          await fetchData();
          handleCloseDialog();
        }
      } else {
        const result = await createPricingRule(token, submitData);
        if (!result?.error) {
          await fetchData();
          handleCloseDialog();
        }
      }
    } catch (error) {
      console.error('Error saving pricing rule:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Ar tikrai norite pašalinti šią kainų taisyklę?')) {
      try {
        await deletePricingRule(token, id);
        await fetchData();
      } catch (error) {
        console.error('Error deleting pricing rule:', error);
      }
    }
  };

  const getCourtName = (courtId, sport) => {
    const court = courts.find((c) => c.courtId === courtId && c.sport === sport);
    return court ? court.courtName : `Aikštelė ${courtId}`;
  };

  const getSportDisplayName = (sport) => {
    switch (sport) {
      case 'Padelis':
        return 'Padelis';
      case 'Tinklinis':
        return 'Tinklinis';
      case 'Beachtennis':
        return 'Beach Tennis';
      default:
        return sport;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Kraunama...</Typography>
      </Box>
    );
  }


  return (
    <Box sx={{ p: 3, mt: 8 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          width: '100%',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Typography variant="h4" component="h1" sx={{ flex: 1 }}>
          Laikotarpio kainų valdymas
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            backgroundColor: '#1976d2',
            color: 'white',
            px: 3,
            py: 1.5,
            fontSize: '14px',
            fontWeight: '500',
            textTransform: 'none',
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: '#1565c0',
              boxShadow: '0 4px 8px rgba(25, 118, 210, 0.3)'
            }
          }}
        >
          Pridėti kainų taisyklę
        </Button>
      </Box>


      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Aikštelė</TableCell>
                <TableCell>Sportas</TableCell>
                <TableCell>Laikotarpis</TableCell>
                <TableCell>Darbo dienos iki 17h</TableCell>
                <TableCell>Darbo dienos po 17h</TableCell>
                <TableCell>Savaitgaliai iki 17h</TableCell>
                <TableCell>Savaitgaliai po 17h</TableCell>
                <TableCell>Veiksmai</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pricingRules.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell>{getCourtName(rule.court_id, rule.sport)}</TableCell>
                  <TableCell>
                    <Chip
                      label={getSportDisplayName(rule.sport)}
                      size="small"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {moment(rule.start_date).format('YYYY-MM-DD')} -{' '}
                      {moment(rule.end_date).format('YYYY-MM-DD')}
                    </Typography>
                  </TableCell>
                  <TableCell>€{rule.weekday_price_until_17}</TableCell>
                  <TableCell>€{rule.weekday_price_after_17}</TableCell>
                  <TableCell>€{rule.weekend_price_until_17}</TableCell>
                  <TableCell>€{rule.weekend_price_after_17}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleOpenDialog(rule)}
                      color="primary"
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(rule.id)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {pricingRules.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="textSecondary">
                      Nėra sukurtų kainų taisyklių
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRule ? 'Redaguoti kainų taisyklę' : 'Sukurti naują kainų taisyklę'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Sportas</InputLabel>
              <Select
                value={formData.sport}
                onChange={(e) => handleSportChange(e.target.value)}
                label="Sportas"
              >
                <MenuItem value="Padelis">Padelis</MenuItem>
                <MenuItem value="Tinklinis">Tinklinis</MenuItem>
                <MenuItem value="Beachtennis">Beach Tennis</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={!formData.sport}>
              <InputLabel>Aikštelė</InputLabel>
              <Select
                value={formData.court_id}
                onChange={(e) => handleInputChange('court_id', e.target.value)}
                label="Aikštelė"
              >
                {getFilteredCourts().map((court, index) => (
                  <MenuItem
                    key={`${court.courtId}-${court.sport}-${index}`}
                    value={court.courtId}
                  >
                    {court.courtName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="Pradžios data"
                  value={formData.start_date}
                  onChange={(value) => handleInputChange('start_date', value)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
                <DatePicker
                  label="Pabaigos data"
                  value={formData.end_date}
                  onChange={(value) => handleInputChange('end_date', value)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Box>

            <Typography variant="h6" sx={{ mt: 2 }}>
              Kainos (€)
            </Typography>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Darbo dienos iki 17h"
                type="number"
                value={formData.weekday_price_until_17}
                onChange={(e) => handleInputChange('weekday_price_until_17', e.target.value)}
                inputProps={{ step: '0.01', min: '0' }}
              />
              <TextField
                fullWidth
                label="Darbo dienos po 17h"
                type="number"
                value={formData.weekday_price_after_17}
                onChange={(e) => handleInputChange('weekday_price_after_17', e.target.value)}
                inputProps={{ step: '0.01', min: '0' }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Savaitgaliai iki 17h"
                type="number"
                value={formData.weekend_price_until_17}
                onChange={(e) => handleInputChange('weekend_price_until_17', e.target.value)}
                inputProps={{ step: '0.01', min: '0' }}
              />
              <TextField
                fullWidth
                label="Savaitgaliai po 17h"
                type="number"
                value={formData.weekend_price_after_17}
                onChange={(e) => handleInputChange('weekend_price_after_17', e.target.value)}
                inputProps={{ step: '0.01', min: '0' }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Atšaukti</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ backgroundColor: '#e63946' }}
            disabled={
              !formData.court_id ||
              !formData.sport ||
              !formData.start_date ||
              !formData.end_date ||
              !formData.weekday_price_until_17 ||
              !formData.weekday_price_after_17 ||
              !formData.weekend_price_until_17 ||
              !formData.weekend_price_after_17
            }
          >
            {editingRule ? 'Atnaujinti' : 'Sukurti'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DateRangePricing;